import React, { useState, useEffect } from 'react';
import {
  Search,
  Filter,
  Loader2,
  AlertCircle,
  Eye,
  Download,
  Printer,
  RefreshCw,
  Receipt,
  DollarSign,
  CheckCircle2,
  Clock,
  TrendingUp,
  X,
  Copy,
  Check,
  ExternalLink,
  ShieldCheck,
  Calendar,
  Building,
  User,
  Mail,
  Phone,
  FileText,
  CreditCard
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';

interface PaymentRecord {
  id: number;
  reference_number: string;
  customer_name: string;
  customer_email: string;
  customer_phone: string;
  company_name?: string;
  service_description: string;
  additional_notes?: string;
  amount: number | string;
  currency: string;
  payment_status: string;
  stripe_session_id?: string;
  stripe_payment_intent_id?: string;
  stripe_customer_id?: string;
  receipt_number?: string;
  receipt_url?: string;
  paid_at?: string;
  created_at: string;
  updated_at: string;
}

interface SummaryStats {
  total_revenue: number;
  paid_count: number;
  pending_count: number;
  avg_amount: number;
}

export default function OtherServicePayments() {
  const { logout } = useAuth();
  const navigate = useNavigate();

  const [payments, setPayments] = useState<PaymentRecord[]>([]);
  const [stats, setStats] = useState<SummaryStats>({
    total_revenue: 0,
    paid_count: 0,
    pending_count: 0,
    avg_amount: 0,
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters & Search
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const perPage = 15;

  // Modals state
  const [selectedPayment, setSelectedPayment] = useState<PaymentRecord | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isReceiptModalOpen, setIsReceiptModalOpen] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const API_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api';

  const fetchPayments = async (page = 1) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: perPage.toString(),
      });

      if (searchTerm.trim()) params.append('search', searchTerm.trim());
      if (selectedStatus !== 'all') params.append('status', selectedStatus);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);

      const response = await fetch(`${API_URL}/instructor/other-service-payments?${params.toString()}`, {
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (response.status === 401) {
        logout();
        navigate('/instructor-login');
        return;
      }

      const resData = await response.json();

      if (response.ok) {
        setPayments(resData.data || []);
        setCurrentPage(resData.current_page || 1);
        setTotalPages(resData.last_page || 1);
        setTotalCount(resData.total || 0);
        if (resData.stats) {
          setStats(resData.stats);
        }
      } else {
        throw new Error(resData.message || 'Failed to fetch payment records.');
      }
    } catch (err: any) {
      console.error('Fetch payments error:', err);
      setError(err.message || 'Failed to load records.');
      toast.error(err.message || 'Error loading records.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayments(1);
  }, [selectedStatus, dateFrom, dateTo]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    fetchPayments(1);
  };

  const handleResetFilters = () => {
    setSearchTerm('');
    setSelectedStatus('all');
    setDateFrom('');
    setDateTo('');
    setCurrentPage(1);
  };

  const handleCopy = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(label);
    toast.success(`${label} copied!`);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleDownloadReceipt = (payment: PaymentRecord) => {
    const receiptImgUrl = `${API_URL}/other-services/payments/${payment.id}/receipt-image`;
    const link = document.createElement('a');
    link.href = receiptImgUrl;
    link.download = `receipt_${payment.reference_number}.png`;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Downloading receipt...');
  };

  const handlePrintReceipt = (payment: PaymentRecord) => {
    const receiptImgUrl = `${API_URL}/other-services/payments/${payment.id}/receipt-image`;
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>Receipt - ${payment.reference_number}</title>
            <style>
              body { margin: 0; display: flex; justify-content: center; align-items: center; background: #fff; font-family: sans-serif; }
              img { max-width: 100%; height: auto; display: block; margin: 20px auto; }
              @media print {
                body { margin: 0; }
                img { width: 100%; }
              }
            </style>
          </head>
          <body>
            <img src="${receiptImgUrl}" alt="Official Receipt" onload="window.print();" />
          </body>
        </html>
      `);
      printWindow.document.close();
    }
  };

  const getStatusBadge = (status: string) => {
    const st = (status || 'pending').toLowerCase();
    switch (st) {
      case 'paid':
        return (
          <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            background: 'rgba(16, 185, 129, 0.15)',
            color: '#10b981',
            border: '1px solid rgba(16, 185, 129, 0.3)',
            padding: '3px 10px',
            borderRadius: '99px',
            fontSize: '0.75rem',
            fontWeight: 700,
            textTransform: 'uppercase'
          }}>
            <CheckCircle2 size={12} /> Paid
          </span>
        );
      case 'pending':
        return (
          <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            background: 'rgba(245, 158, 11, 0.15)',
            color: '#f59e0b',
            border: '1px solid rgba(245, 158, 11, 0.3)',
            padding: '3px 10px',
            borderRadius: '99px',
            fontSize: '0.75rem',
            fontWeight: 700,
            textTransform: 'uppercase'
          }}>
            <Clock size={12} /> Pending
          </span>
        );
      case 'failed':
        return (
          <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            background: 'rgba(239, 68, 68, 0.15)',
            color: '#ef4444',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            padding: '3px 10px',
            borderRadius: '99px',
            fontSize: '0.75rem',
            fontWeight: 700,
            textTransform: 'uppercase'
          }}>
            <AlertCircle size={12} /> Failed
          </span>
        );
      case 'cancelled':
        return (
          <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            background: 'rgba(148, 163, 184, 0.15)',
            color: '#94a3b8',
            border: '1px solid rgba(148, 163, 184, 0.3)',
            padding: '3px 10px',
            borderRadius: '99px',
            fontSize: '0.75rem',
            fontWeight: 700,
            textTransform: 'uppercase'
          }}>
            Cancelled
          </span>
        );
      case 'refunded':
        return (
          <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            background: 'rgba(168, 85, 247, 0.15)',
            color: '#c084fc',
            border: '1px solid rgba(168, 85, 247, 0.3)',
            padding: '3px 10px',
            borderRadius: '99px',
            fontSize: '0.75rem',
            fontWeight: 700,
            textTransform: 'uppercase'
          }}>
            Refunded
          </span>
        );
      default:
        return (
          <span style={{
            background: 'rgba(148, 163, 184, 0.15)',
            color: '#94a3b8',
            padding: '3px 10px',
            borderRadius: '99px',
            fontSize: '0.75rem',
            fontWeight: 700,
            textTransform: 'uppercase'
          }}>
            {status}
          </span>
        );
    }
  };

  return (
    <div className="osp-admin-container">
      <style dangerouslySetInnerHTML={{
        __html: `
        .osp-admin-container {
          padding: 1.5rem 0.5rem;
        }

        .osp-page-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 1rem;
          margin-bottom: 2rem;
        }

        .osp-header-title {
          font-size: 1.75rem;
          font-weight: 850;
          color: var(--index-text-primary, #ffffff);
          margin-bottom: 0.35rem;
        }

        .osp-header-subtitle {
          color: var(--index-text-secondary, #94a3b8);
          font-size: 0.95rem;
        }

        /* Stats Cards */
        .osp-stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
          gap: 1.25rem;
          margin-bottom: 2rem;
        }

        .osp-stat-card {
          background: var(--bg-card, rgba(30, 41, 59, 0.6));
          border: 1px solid var(--border-color, rgba(255, 255, 255, 0.08));
          border-radius: 16px;
          padding: 1.25rem 1.5rem;
          display: flex;
          align-items: center;
          gap: 1.25rem;
          transition: transform 0.2s, box-shadow 0.2s;
        }

        .osp-stat-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
        }

        .osp-stat-icon-wrap {
          width: 52px;
          height: 52px;
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .osp-stat-val {
          font-size: 1.5rem;
          font-weight: 850;
          color: var(--index-text-primary, #ffffff);
          line-height: 1.2;
        }

        .osp-stat-lbl {
          font-size: 0.8rem;
          color: var(--index-text-secondary, #94a3b8);
          font-weight: 600;
          text-transform: uppercase;
          margin-top: 2px;
        }

        /* Filter bar */
        .osp-filter-bar {
          background: var(--bg-card, rgba(30, 41, 59, 0.6));
          border: 1px solid var(--border-color, rgba(255, 255, 255, 0.08));
          border-radius: 16px;
          padding: 1.25rem;
          margin-bottom: 1.75rem;
          display: flex;
          flex-wrap: wrap;
          gap: 1rem;
          align-items: center;
          justify-content: space-between;
        }

        .osp-search-form {
          display: flex;
          align-items: center;
          position: relative;
          flex: 1;
          min-width: 260px;
        }

        .osp-search-input {
          width: 100%;
          background: rgba(15, 23, 42, 0.6);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 10px;
          color: #ffffff;
          padding: 0.65rem 1rem 0.65rem 2.4rem;
          font-size: 0.9rem;
          outline: none;
          transition: all 0.2s;
        }

        .osp-search-input:focus {
          border-color: #5eead4;
          box-shadow: 0 0 0 2px rgba(94, 234, 212, 0.15);
        }

        .osp-filter-group {
          display: flex;
          flex-wrap: wrap;
          gap: 0.75rem;
          align-items: center;
        }

        .osp-select-filter {
          background: rgba(15, 23, 42, 0.6);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 10px;
          color: #cbd5e1;
          padding: 0.65rem 1rem;
          font-size: 0.85rem;
          outline: none;
          cursor: pointer;
        }

        .osp-date-input {
          background: rgba(15, 23, 42, 0.6);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 10px;
          color: #cbd5e1;
          padding: 0.55rem 0.75rem;
          font-size: 0.85rem;
          outline: none;
        }

        .osp-btn-refresh {
          background: rgba(255, 255, 255, 0.08);
          border: 1px solid rgba(255, 255, 255, 0.12);
          color: #cbd5e1;
          padding: 0.65rem 0.9rem;
          border-radius: 10px;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-size: 0.85rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .osp-btn-refresh:hover {
          background: rgba(94, 234, 212, 0.15);
          color: #5eead4;
          border-color: #5eead4;
        }

        /* Table Styles */
        .osp-table-card {
          background: var(--bg-card, rgba(30, 41, 59, 0.6));
          border: 1px solid var(--border-color, rgba(255, 255, 255, 0.08));
          border-radius: 16px;
          overflow: hidden;
          box-shadow: 0 12px 30px rgba(0, 0, 0, 0.2);
        }

        .osp-table {
          width: 100%;
          border-collapse: collapse;
          text-align: left;
        }

        .osp-table th {
          background: rgba(15, 23, 42, 0.8);
          color: #94a3b8;
          font-size: 0.75rem;
          font-weight: 750;
          text-transform: uppercase;
          letter-spacing: 0.6px;
          padding: 1rem 1.25rem;
          border-bottom: 1px solid rgba(255, 255, 255, 0.08);
        }

        .osp-table td {
          padding: 1rem 1.25rem;
          border-bottom: 1px solid rgba(255, 255, 255, 0.04);
          font-size: 0.875rem;
          color: #cbd5e1;
        }

        .osp-table tbody tr {
          transition: background 0.15s;
        }

        .osp-table tbody tr:hover {
          background: rgba(255, 255, 255, 0.025);
        }

        .osp-action-btn {
          background: rgba(255, 255, 255, 0.06);
          border: 1px solid rgba(255, 255, 255, 0.1);
          color: #cbd5e1;
          width: 32px;
          height: 32px;
          border-radius: 8px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s;
          margin-right: 6px;
        }

        .osp-action-btn:hover {
          background: rgba(94, 234, 212, 0.15);
          color: #5eead4;
          border-color: #5eead4;
          transform: translateY(-1px);
        }

        /* Modal Backdrop & Container */
        .osp-modal-backdrop {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.75);
          backdrop-filter: blur(6px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
          padding: 1rem;
        }

        .osp-modal-content {
          background: #0f172a;
          border: 1px solid rgba(94, 234, 212, 0.25);
          border-radius: 20px;
          width: 100%;
          max-width: 640px;
          max-height: 90vh;
          overflow-y: auto;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
          padding: 2rem;
          position: relative;
        }

        .osp-modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1.5rem;
          padding-bottom: 1rem;
          border-bottom: 1px solid rgba(255, 255, 255, 0.08);
        }

        .osp-modal-close {
          background: rgba(255, 255, 255, 0.08);
          border: none;
          color: #94a3b8;
          width: 32px;
          height: 32px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s;
        }

        .osp-modal-close:hover {
          background: rgba(239, 68, 68, 0.2);
          color: #ef4444;
        }

        .osp-info-card {
          background: rgba(30, 41, 59, 0.5);
          border: 1px solid rgba(255, 255, 255, 0.06);
          border-radius: 12px;
          padding: 1.25rem;
          margin-bottom: 1.25rem;
        }

        .osp-info-card-title {
          font-size: 0.85rem;
          font-weight: 750;
          color: #5eead4;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 0.75rem;
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .osp-info-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 0.85rem;
          font-size: 0.85rem;
        }

        @media (max-width: 500px) {
          .osp-info-grid {
            grid-template-columns: 1fr;
          }
        }

        .osp-info-lbl {
          color: #94a3b8;
          font-size: 0.75rem;
          margin-bottom: 2px;
        }

        .osp-info-val {
          color: #ffffff;
          font-weight: 600;
          word-break: break-all;
        }

        /* Pagination Controls */
        .osp-pagination {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1.25rem;
          border-top: 1px solid rgba(255, 255, 255, 0.06);
          flex-wrap: wrap;
          gap: 1rem;
        }

        .osp-page-btn {
          background: rgba(255, 255, 255, 0.08);
          border: 1px solid rgba(255, 255, 255, 0.12);
          color: #cbd5e1;
          padding: 6px 14px;
          border-radius: 8px;
          font-size: 0.85rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .osp-page-btn:hover:not(:disabled) {
          background: rgba(94, 234, 212, 0.15);
          color: #5eead4;
          border-color: #5eead4;
        }

        .osp-page-btn:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }
        `
      }} />

      {/* Page Header */}
      <div className="osp-page-header">
        <div>
          <h1 className="osp-header-title">Other Service Payments</h1>
          <p className="osp-subtitle">
            Manage and track payments received for consulting, corporate workshops, certification processing, and specialized advisory services.
          </p>
        </div>
        <div>
          <button className="osp-btn-refresh" onClick={() => fetchPayments(currentPage)}>
            <RefreshCw size={15} className={loading ? 'animate-spin' : ''} /> Refresh Records
          </button>
        </div>
      </div>

      {/* Stats Summary Cards */}
      <div className="osp-stats-grid">
        <div className="osp-stat-card">
          <div className="osp-stat-icon-wrap" style={{ background: 'rgba(16, 185, 129, 0.15)', color: '#10b981' }}>
            <DollarSign size={26} />
          </div>
          <div>
            <div className="osp-stat-val">
              ${stats.total_revenue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
            <div className="osp-stat-lbl">Total Settled Revenue</div>
          </div>
        </div>

        <div className="osp-stat-card">
          <div className="osp-stat-icon-wrap" style={{ background: 'rgba(94, 234, 212, 0.15)', color: '#5eead4' }}>
            <CheckCircle2 size={26} />
          </div>
          <div>
            <div className="osp-stat-val">{stats.paid_count}</div>
            <div className="osp-stat-lbl">Paid Transactions</div>
          </div>
        </div>

        <div className="osp-stat-card">
          <div className="osp-stat-icon-wrap" style={{ background: 'rgba(245, 158, 11, 0.15)', color: '#f59e0b' }}>
            <Clock size={26} />
          </div>
          <div>
            <div className="osp-stat-val">{stats.pending_count}</div>
            <div className="osp-stat-lbl">Pending Settlements</div>
          </div>
        </div>

        <div className="osp-stat-card">
          <div className="osp-stat-icon-wrap" style={{ background: 'rgba(56, 189, 248, 0.15)', color: '#38bdf8' }}>
            <TrendingUp size={26} />
          </div>
          <div>
            <div className="osp-stat-val">
              ${stats.avg_amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
            <div className="osp-stat-lbl">Average Transaction</div>
          </div>
        </div>
      </div>

      {/* Filter & Search Bar */}
      <div className="osp-filter-bar">
        <form onSubmit={handleSearchSubmit} className="osp-search-form">
          <Search size={16} style={{ position: 'absolute', left: '12px', color: '#64748b' }} />
          <input
            type="text"
            className="osp-search-input"
            placeholder="Search by customer name, email, phone, reference, or receipt #..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </form>

        <div className="osp-filter-group">
          {/* Status filter */}
          <select
            className="osp-select-filter"
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
          >
            <option value="all">All Statuses</option>
            <option value="paid">Paid</option>
            <option value="pending">Pending</option>
            <option value="failed">Failed</option>
            <option value="cancelled">Cancelled</option>
            <option value="refunded">Refunded</option>
          </select>

          {/* Date from */}
          <input
            type="date"
            className="osp-date-input"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            title="Start Date"
          />

          {/* Date to */}
          <input
            type="date"
            className="osp-date-input"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            title="End Date"
          />

          {(searchTerm || selectedStatus !== 'all' || dateFrom || dateTo) && (
            <button
              type="button"
              className="osp-btn-refresh"
              onClick={handleResetFilters}
              style={{ color: '#ef4444', borderColor: 'rgba(239, 68, 68, 0.3)' }}
            >
              <X size={14} /> Clear
            </button>
          )}
        </div>
      </div>

      {/* Data Table */}
      <div className="osp-table-card">
        {loading ? (
          <div style={{ padding: '4rem', textAlign: 'center', color: '#94a3b8' }}>
            <Loader2 size={36} className="animate-spin" style={{ margin: '0 auto 1rem', color: '#5eead4' }} />
            <p style={{ margin: 0, fontWeight: 600 }}>Loading payment records...</p>
          </div>
        ) : error ? (
          <div style={{ padding: '3rem', textAlign: 'center', color: '#ef4444' }}>
            <AlertCircle size={36} style={{ margin: '0 auto 1rem' }} />
            <p style={{ margin: 0, fontWeight: 600 }}>{error}</p>
            <button
              className="osp-btn-refresh"
              onClick={() => fetchPayments(currentPage)}
              style={{ marginTop: '1rem' }}
            >
              Try Again
            </button>
          </div>
        ) : payments.length === 0 ? (
          <div style={{ padding: '4rem', textAlign: 'center', color: '#94a3b8' }}>
            <Receipt size={40} style={{ margin: '0 auto 1rem', opacity: 0.4 }} />
            <h3 style={{ fontSize: '1.15rem', color: '#f1f5f9', marginBottom: '0.5rem' }}>No Payment Records Found</h3>
            <p style={{ margin: 0, fontSize: '0.9rem', maxWidth: '420px', marginInline: 'auto' }}>
              No payments match your current search and filter parameters. Try clearing the filter criteria.
            </p>
          </div>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table className="osp-table">
                <thead>
                  <tr>
                    <th>Reference #</th>
                    <th>Customer</th>
                    <th>Service Scope</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Date</th>
                    <th>Receipt #</th>
                    <th style={{ textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((p) => (
                    <tr key={p.id}>
                      {/* Reference # */}
                      <td>
                        <span style={{
                          fontFamily: 'monospace',
                          fontWeight: 700,
                          color: '#5eead4',
                          background: 'rgba(94, 234, 212, 0.08)',
                          padding: '3px 8px',
                          borderRadius: '6px',
                          border: '1px solid rgba(94, 234, 212, 0.2)'
                        }}>
                          {p.reference_number}
                        </span>
                      </td>

                      {/* Customer info */}
                      <td>
                        <div style={{ fontWeight: 700, color: '#f8fafc' }}>{p.customer_name}</div>
                        <div style={{ fontSize: '0.78rem', color: '#94a3b8', fontFamily: 'monospace' }}>{p.customer_email}</div>
                        {p.company_name && (
                          <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{p.company_name}</div>
                        )}
                      </td>

                      {/* Service scope */}
                      <td>
                        <div style={{ maxWidth: '240px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={p.service_description}>
                          {p.service_description}
                        </div>
                      </td>

                      {/* Amount */}
                      <td>
                        <span style={{ fontWeight: 800, color: '#f8fafc' }}>
                          ${parseFloat(String(p.amount)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                        <span style={{ fontSize: '0.75rem', color: '#94a3b8', marginLeft: '4px' }}>
                          {p.currency.toUpperCase()}
                        </span>
                      </td>

                      {/* Status */}
                      <td>{getStatusBadge(p.payment_status)}</td>

                      {/* Date */}
                      <td style={{ fontSize: '0.8rem', color: '#94a3b8' }}>
                        {p.paid_at ? new Date(p.paid_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : new Date(p.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </td>

                      {/* Receipt # */}
                      <td>
                        <span style={{ fontFamily: 'monospace', fontSize: '0.8rem', color: p.receipt_number ? '#cbd5e1' : '#64748b' }}>
                          {p.receipt_number || 'Pending'}
                        </span>
                      </td>

                      {/* Actions */}
                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <button
                          className="osp-action-btn"
                          title="View Payment Details"
                          onClick={() => {
                            setSelectedPayment(p);
                            setIsDetailModalOpen(true);
                          }}
                        >
                          <Eye size={15} />
                        </button>

                        <button
                          className="osp-action-btn"
                          title="View Official Receipt"
                          onClick={() => {
                            setSelectedPayment(p);
                            setIsReceiptModalOpen(true);
                          }}
                        >
                          <Receipt size={15} />
                        </button>

                        <button
                          className="osp-action-btn"
                          title="Download Receipt Image"
                          onClick={() => handleDownloadReceipt(p)}
                        >
                          <Download size={15} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            <div className="osp-pagination">
              <div style={{ fontSize: '0.85rem', color: '#94a3b8' }}>
                Showing {(currentPage - 1) * perPage + 1} to {Math.min(currentPage * perPage, totalCount)} of {totalCount} payment records
              </div>

              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <button
                  className="osp-page-btn"
                  disabled={currentPage <= 1}
                  onClick={() => fetchPayments(currentPage - 1)}
                >
                  Previous
                </button>

                <span style={{ fontSize: '0.85rem', color: '#cbd5e1', fontWeight: 600, padding: '0 8px' }}>
                  Page {currentPage} of {totalPages}
                </span>

                <button
                  className="osp-page-btn"
                  disabled={currentPage >= totalPages}
                  onClick={() => fetchPayments(currentPage + 1)}
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Payment Details Modal */}
      {isDetailModalOpen && selectedPayment && (
        <div className="osp-modal-backdrop" onClick={() => setIsDetailModalOpen(false)}>
          <div className="osp-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="osp-modal-header">
              <div>
                <h2 style={{ fontSize: '1.25rem', fontWeight: 800, color: '#ffffff', margin: 0 }}>
                  Payment Details
                </h2>
                <span style={{ fontFamily: 'monospace', fontSize: '0.85rem', color: '#5eead4' }}>
                  {selectedPayment.reference_number}
                </span>
              </div>
              <button className="osp-modal-close" onClick={() => setIsDetailModalOpen(false)}>
                <X size={16} />
              </button>
            </div>

            {/* Customer Info Card */}
            <div className="osp-info-card">
              <div className="osp-info-card-title">
                <User size={15} /> Customer Details
              </div>
              <div className="osp-info-grid">
                <div>
                  <div className="osp-info-lbl">Full Name</div>
                  <div className="osp-info-val">{selectedPayment.customer_name}</div>
                </div>
                <div>
                  <div className="osp-info-lbl">Email Address</div>
                  <div className="osp-info-val">{selectedPayment.customer_email}</div>
                </div>
                <div>
                  <div className="osp-info-lbl">Phone Number</div>
                  <div className="osp-info-val">{selectedPayment.customer_phone || 'N/A'}</div>
                </div>
                <div>
                  <div className="osp-info-lbl">Company / Org</div>
                  <div className="osp-info-val">{selectedPayment.company_name || 'Individual / None'}</div>
                </div>
              </div>
            </div>

            {/* Service & Notes Card */}
            <div className="osp-info-card">
              <div className="osp-info-card-title">
                <FileText size={15} /> Service & Scope
              </div>
              <div style={{ marginBottom: '0.75rem' }}>
                <div className="osp-info-lbl">Service Description</div>
                <div style={{ color: '#ffffff', fontWeight: 600, fontSize: '0.9rem', lineHeight: 1.5 }}>
                  {selectedPayment.service_description}
                </div>
              </div>
              {selectedPayment.additional_notes && (
                <div>
                  <div className="osp-info-lbl">Customer Notes / PO Ref</div>
                  <div style={{ color: '#cbd5e1', fontSize: '0.85rem', background: 'rgba(15, 23, 42, 0.5)', padding: '8px 12px', borderRadius: '6px', border: '1px solid rgba(255, 255, 255, 0.04)' }}>
                    {selectedPayment.additional_notes}
                  </div>
                </div>
              )}
            </div>

            {/* Financial & Stripe Meta */}
            <div className="osp-info-card">
              <div className="osp-info-card-title">
                <CreditCard size={15} /> Transaction & Stripe Details
              </div>
              <div className="osp-info-grid">
                <div>
                  <div className="osp-info-lbl">Amount Paid</div>
                  <div className="osp-info-val" style={{ color: '#5eead4', fontSize: '1.1rem', fontWeight: 800 }}>
                    ${parseFloat(String(selectedPayment.amount)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {selectedPayment.currency.toUpperCase()}
                  </div>
                </div>
                <div>
                  <div className="osp-info-lbl">Payment Status</div>
                  <div>{getStatusBadge(selectedPayment.payment_status)}</div>
                </div>
                <div>
                  <div className="osp-info-lbl">Receipt Number</div>
                  <div className="osp-info-val" style={{ fontFamily: 'monospace' }}>
                    {selectedPayment.receipt_number || 'N/A'}
                  </div>
                </div>
                <div>
                  <div className="osp-info-lbl">Paid Timestamp</div>
                  <div className="osp-info-val">
                    {selectedPayment.paid_at ? new Date(selectedPayment.paid_at).toLocaleString() : 'Unsettled'}
                  </div>
                </div>
                {selectedPayment.stripe_payment_intent_id && (
                  <div style={{ gridColumn: 'span 2' }}>
                    <div className="osp-info-lbl">Stripe Payment Intent ID</div>
                    <div className="osp-info-val" style={{ fontFamily: 'monospace', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      {selectedPayment.stripe_payment_intent_id}
                      <button
                        onClick={() => handleCopy(selectedPayment.stripe_payment_intent_id!, 'Payment Intent ID')}
                        style={{ background: 'transparent', border: 'none', color: '#94a3b8', cursor: 'pointer' }}
                        title="Copy"
                      >
                        <Copy size={12} />
                      </button>
                    </div>
                  </div>
                )}
                {selectedPayment.stripe_session_id && (
                  <div style={{ gridColumn: 'span 2' }}>
                    <div className="osp-info-lbl">Stripe Checkout Session ID</div>
                    <div className="osp-info-val" style={{ fontFamily: 'monospace', fontSize: '0.78rem', color: '#94a3b8' }}>
                      {selectedPayment.stripe_session_id}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Modal Actions */}
            <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem', justifyContent: 'flex-end' }}>
              <button
                className="osp-btn-refresh"
                onClick={() => {
                  setIsDetailModalOpen(false);
                  setIsReceiptModalOpen(true);
                }}
              >
                <Receipt size={15} /> Preview Receipt
              </button>
              <button
                className="osp-btn-refresh"
                style={{ background: '#1a4d3e', color: '#ffffff', borderColor: '#5eead4' }}
                onClick={() => handleDownloadReceipt(selectedPayment)}
              >
                <Download size={15} /> Download PNG Receipt
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Receipt Preview Modal */}
      {isReceiptModalOpen && selectedPayment && (
        <div className="osp-modal-backdrop" onClick={() => setIsReceiptModalOpen(false)}>
          <div className="osp-modal-content" style={{ maxWidth: '780px' }} onClick={(e) => e.stopPropagation()}>
            <div className="osp-modal-header">
              <div>
                <h2 style={{ fontSize: '1.25rem', fontWeight: 800, color: '#ffffff', margin: 0 }}>
                  Official Payment Receipt
                </h2>
                <span style={{ fontFamily: 'monospace', fontSize: '0.85rem', color: '#5eead4' }}>
                  {selectedPayment.reference_number}
                </span>
              </div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <button className="osp-btn-refresh" onClick={() => handlePrintReceipt(selectedPayment)}>
                  <Printer size={15} /> Print
                </button>
                <button
                  className="osp-btn-refresh"
                  style={{ background: '#1a4d3e', color: '#ffffff', borderColor: '#5eead4' }}
                  onClick={() => handleDownloadReceipt(selectedPayment)}
                >
                  <Download size={15} /> Download PNG
                </button>
                <button className="osp-modal-close" onClick={() => setIsReceiptModalOpen(false)}>
                  <X size={16} />
                </button>
              </div>
            </div>

            {/* Embedded Receipt Image */}
            <div style={{ textAlign: 'center', background: '#090e17', borderRadius: '12px', padding: '1rem', border: '1px solid rgba(255, 255, 255, 0.08)' }}>
              <img
                src={`${API_URL}/other-services/payments/${selectedPayment.id}/receipt-image`}
                alt="Official Payment Receipt"
                style={{ maxWidth: '100%', height: 'auto', borderRadius: '8px', boxShadow: '0 8px 24px rgba(0, 0, 0, 0.4)' }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
